import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../components/DashboardLayout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Eye, Clock, CheckCircle2, AlertCircle, XCircle, ExternalLink, Copy, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function V2TestHistory() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [integrity, setIntegrity] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/v2/interviews`, { headers });
        setInterviews(res.data);
      } catch (err) { toast.error('Failed to load history'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const viewDetail = async (iv) => {
    setSelectedInterview(iv);
    setDetailOpen(true);
    try {
      const [planRes, intRes] = await Promise.all([
        axios.get(`${API_URL}/api/v2/interviews/${iv.id}/plan`, { headers }),
        axios.get(`${API_URL}/api/v2/interviews/${iv.id}/integrity`, { headers })
      ]);
      setSelectedInterview({ ...iv, plan: planRes.data, full: true });
      setIntegrity(intRes.data);
    } catch (err) { console.error(err); }
  };

  const statusBadge = (status) => {
    const map = {
      created: { variant: 'outline', icon: Clock, color: 'text-blue-500' },
      in_progress: { variant: 'outline', icon: Clock, color: 'text-amber-500' },
      completed: { variant: 'default', icon: CheckCircle2, color: 'text-green-500' },
      time_limit_reached: { variant: 'secondary', icon: AlertCircle, color: 'text-amber-500' },
      failed: { variant: 'destructive', icon: XCircle, color: 'text-red-500' }
    };
    const m = map[status] || map.created;
    return <Badge variant={m.variant} className="gap-1"><m.icon className={`w-3 h-3 ${m.color}`} />{status}</Badge>;
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-4" data-testid="v2-test-history">
        <h1 className="text-2xl font-bold">V2 Test History</h1>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-muted/50">
                  <th className="p-3 text-left font-medium">Interview</th>
                  <th className="p-3 text-left font-medium">Student</th>
                  <th className="p-3 text-left font-medium">Questions</th>
                  <th className="p-3 text-left font-medium">Answered</th>
                  <th className="p-3 text-left font-medium">Status</th>
                  <th className="p-3 text-left font-medium">Created</th>
                  <th className="p-3 text-right font-medium">Actions</th>
                </tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Loading...</td></tr>
                  ) : interviews.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No V2 test interviews yet.</td></tr>
                  ) : interviews.map(iv => (
                    <tr key={iv.id} className="border-b hover:bg-muted/30">
                      <td className="p-3 font-mono text-xs">{iv.interview_number}</td>
                      <td className="p-3">{iv.student_name}</td>
                      <td className="p-3 font-semibold">{iv.planned_question_count}</td>
                      <td className="p-3">{iv.answered_question_count || 0}</td>
                      <td className="p-3">{statusBadge(iv.status)}</td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(iv.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => viewDetail(iv)} data-testid={`v2-view-${iv.id}`}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/v2/interviews/${iv.id}`)} data-testid={`v2-open-detail-${iv.id}`}><FileText className="w-4 h-4" /></Button>
                        {iv.status === 'created' && (
                          <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/interview/v2/${iv.interview_token}`); toast.success('Link copied'); }}>
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t text-sm text-muted-foreground">{interviews.length} interview(s)</div>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Interview Detail — {selectedInterview?.interview_number}</DialogTitle></DialogHeader>
            {selectedInterview && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Student</span><p className="font-medium">{selectedInterview.student_name}</p></div>
                  <div><span className="text-muted-foreground">Status</span><p>{statusBadge(selectedInterview.status)}</p></div>
                  <div><span className="text-muted-foreground">Engine</span><Badge>V2</Badge></div>
                  <div><span className="text-muted-foreground">Created</span><p className="text-xs">{new Date(selectedInterview.created_at).toLocaleString()}</p></div>
                </div>

                {/* Integrity */}
                {integrity && (
                  <Card className={integrity.integrity === 'PASSED' ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'}>
                    <CardContent className="p-4">
                      <h3 className="font-medium flex items-center gap-2 mb-3">
                        {integrity.integrity === 'PASSED' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
                        Interview Integrity: {integrity.integrity}
                      </h3>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-sm">
                        <div><span className="text-muted-foreground text-xs">Configured</span><p className="font-bold">{integrity.configured_questions}</p></div>
                        <div><span className="text-muted-foreground text-xs">Planned</span><p className="font-bold">{integrity.planned_questions}</p></div>
                        <div><span className="text-muted-foreground text-xs">Presented</span><p className="font-bold">{integrity.presented_questions}</p></div>
                        <div><span className="text-muted-foreground text-xs">Answered</span><p className="font-bold">{integrity.answered_questions}</p></div>
                        <div><span className="text-muted-foreground text-xs">Timed Out</span><p className="font-bold">{integrity.timed_out_questions}</p></div>
                        <div><span className="text-muted-foreground text-xs">Termination</span><p className="text-xs font-medium">{integrity.termination_reason || 'N/A'}</p></div>
                      </div>
                      {integrity.issues?.length > 0 && (
                        <div className="mt-2 text-sm text-red-600">{integrity.issues.map((i, idx) => <p key={idx}>{i}</p>)}</div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Question Table */}
                {integrity?.question_table && (
                  <div>
                    <h3 className="font-medium mb-2 text-sm">Question Plan</h3>
                    <div className="border rounded-lg overflow-auto max-h-[300px]">
                      <table className="w-full text-xs">
                        <thead className="bg-muted sticky top-0"><tr>
                          <th className="p-2 text-left">Seq</th>
                          <th className="p-2 text-left">Instance ID</th>
                          <th className="p-2 text-left">Category</th>
                          <th className="p-2 text-left">Answer</th>
                          <th className="p-2 text-left">Video</th>
                          <th className="p-2 text-left">Status</th>
                        </tr></thead>
                        <tbody>{integrity.question_table.map(q => (
                          <tr key={q.sequence} className="border-t">
                            <td className="p-2 font-bold">{q.sequence}</td>
                            <td className="p-2 font-mono text-[10px]">{q.question_instance_id?.slice(0, 8)}...</td>
                            <td className="p-2">{q.category || '—'}</td>
                            <td className="p-2">{q.has_answer ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <XCircle className="w-3.5 h-3.5 text-muted-foreground" />}</td>
                            <td className="p-2">{q.has_video ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <XCircle className="w-3.5 h-3.5 text-muted-foreground" />}</td>
                            <td className="p-2"><Badge variant="outline" className="text-[10px]">{q.question_status}</Badge></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter><Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
